import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'

import {
  LOCAL_STORAGE_SESSION_TOKEN_KEY,
  LOCAL_STORAGE_SESSION_TOKEN_EXPIRES_AT_KEY,
  HTTP_HEADER_SESSION_TOKEN_KEY,
  SESSION_TOKEN_TTL_MS,
} from '../core/constants'
import { ApiModuleOptions } from '../core/api.module-options'

import { LocalStorageService } from './local-storage.service'
import { shareReplay } from 'rxjs/operators'

@Injectable()
export class HttpService {
  constructor(
    private localStorage: LocalStorageService,
    private httpClient: HttpClient,
    private options: ApiModuleOptions) {

  }

  // ===============================================================================================
  // Sessions
  // ===============================================================================================

  async removeSessionToken() {
    delete window['_sessionToken']

    await this.localStorage.remove(LOCAL_STORAGE_SESSION_TOKEN_EXPIRES_AT_KEY)
    return await this.localStorage.remove(LOCAL_STORAGE_SESSION_TOKEN_KEY)
  }

  async getSessionToken() {
    const expiresAt = await this.localStorage.get<number>(LOCAL_STORAGE_SESSION_TOKEN_EXPIRES_AT_KEY)

    if (expiresAt != null && Date.now() > expiresAt) {
      await this.removeSessionToken()
      return null
    }

    return window['_sessionToken'] || await this.localStorage.get<string>(LOCAL_STORAGE_SESSION_TOKEN_KEY)
  }

  async setSessionToken(sessionToken: string, rememberMe: boolean) {
    window['_sessionToken'] = sessionToken

    const ttlMs = rememberMe ? SESSION_TOKEN_TTL_MS.REMEMBER_ME : SESSION_TOKEN_TTL_MS.DEFAULT
    const expiresAt = Date.now() + ttlMs

    await this.localStorage.set(LOCAL_STORAGE_SESSION_TOKEN_KEY, sessionToken)
    await this.localStorage.set(LOCAL_STORAGE_SESSION_TOKEN_EXPIRES_AT_KEY, expiresAt)
  }

  // ===============================================================================================
  // Helpers
  // ===============================================================================================

  async createHeaders(customHeaders: HttpHeaders) {
    const sessionToken = await this.getSessionToken()
    let headers = customHeaders || new HttpHeaders()

    if (sessionToken) {
      headers = headers.set(HTTP_HEADER_SESSION_TOKEN_KEY, sessionToken)
    }

    return headers
  }

  public get serverURL() {
    return this.options.serverURL
  }

  // ===============================================================================================
  // Public Methods
  // ===============================================================================================

  async get<T>(path: string, queryParams?: any, customHeaders?: HttpHeaders) {
    const fullPath = this.serverURL + path
    const headers = await this.createHeaders(customHeaders)

    return this.httpClient.get<T>(fullPath, { params: queryParams, headers }).toPromise()
  }

  async downloadBlob<T>(path: string, queryParams?: any, customHeaders?: HttpHeaders) {
    const fullPath = this.serverURL + path
    const headers = await this.createHeaders(customHeaders)

    return this.httpClient.request<T>('GET', fullPath, { params: queryParams, headers, responseType: 'blob' as any }).toPromise()
  }

  async downloadBlobResponse(path: string, queryParams?: any, customHeaders?: HttpHeaders): Promise<HttpResponse<Blob>> {
    const fullPath = this.serverURL + path
    const headers = await this.createHeaders(customHeaders)

    return this.httpClient.get(fullPath, { params: queryParams, headers, observe: 'response', responseType: 'blob' }).toPromise()
  }

  async post<T>(path: string, body?: any, queryParams?: any, customHeaders?: HttpHeaders) {
    const fullPath = this.serverURL + path
    const headers = await this.createHeaders(customHeaders)

    return this.httpClient.post<T>(fullPath, body, { params: queryParams, headers }).toPromise()
  }

  async put<T>(path: string, body?: any, queryParams?: any, customHeaders?: HttpHeaders) {
    const fullPath = this.serverURL + path
    const headers = await this.createHeaders(customHeaders)

    return this.httpClient.put<T>(fullPath, body, { params: queryParams, headers }).toPromise()
  }

  postWithProgress(path: string, body?: any, queryParams?: any, customHeaders?: HttpHeaders) {
    const fullPath = this.serverURL + path

    return this.httpClient.post(fullPath, body,
      { params: queryParams, reportProgress: true, observe: 'events', headers: customHeaders }).pipe(shareReplay(1))
  }

  async patch<T>(path: string, body?: any, queryParams?: any, customHeaders?: HttpHeaders) {
    const fullPath = this.serverURL + path
    const headers = await this.createHeaders(customHeaders)

    return this.httpClient.patch<T>(fullPath, body, { params: queryParams, headers }).toPromise()
  }

  async delete<T>(path: string, queryParams?: any, customHeaders?: HttpHeaders) {
    const fullPath = this.serverURL + path
    const headers = await this.createHeaders(customHeaders)

    return this.httpClient.delete<T>(fullPath, { params: queryParams, headers }).toPromise()
  }
}
